import { useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Modal } from "@mui/material";
import { useSnackbar } from "notistack";
import { Form, SimpleItem } from "devextreme-react/ui/form";
import { Template } from "devextreme-react/core/template";
import { useTheme } from "@monolith-forensics/monolith-ui";
import CaseStorageAPI from "@/api/storage/index.js";
import { LocationsAPI } from "@/Features/Locations/locations.api";
import { usePermissions } from "@/hooks/usePermissions";
import { getIpAddress } from "@/api";
import {
  db_timestamp,
  getDateFormat,
  validateTimestamp,
} from "@/utils/date-format";
import UserApi from "@/api/users/users.js";
import UserSelectBoxItem from "@/components/SelectBoxItems/UserSelectBoxItem";
import { SignatureField } from "@/Features";
import ToolBarItems from "@/components/ToolBarItems.js";
import Loader from "@/components/Loader.js";

export const DestroyModal = ({
  open,
  handleClose = () => {},
  onSubmit = () => {},
  defaultInfo,
}) => {
  const form = useRef(null);
  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  const destroyedBySig = useRef(null);
  const submitting = useRef(false);

  const { data: users } = useQuery({
    enabled: open,
    queryKey: [
      "users",
      "list",
      { includeObservers: false, includeInactive: false },
    ],
    queryFn: () =>
      UserApi.getUsers({ includeObservers: false, includeInactive: false }),
  });

  const handleSubmit = async (event) => {
    if (form.current.instance.validate().isValid) {
      const data = { ...form.current.instance.option().formData };

      // An e-signature applies the logged-in user's saved signature, so it
      // must not be attributed to anyone else; a hand-drawn signature for
      // another user is fine (they sign on screen).
      // isEmpty() is checked because SignatureField's Clear button leaves
      // isESignature set; a cleared signature has nothing to attribute.
      if (
        destroyedBySig.current.isESignature &&
        !destroyedBySig.current.isEmpty() &&
        data.destroyed_by.user_id !== currentUser.user_id
      ) {
        enqueueSnackbar(
          "An e-signature can only be applied when you are the Destroyed By user. Clear it and have the selected user sign manually.",
          { variant: "error" }
        );
        return;
      }

      if (submitting.current) return;
      submitting.current = true;

      const ipAddress = await getIpAddress();

      const signature = {};

      signature.custody_from_sig = destroyedBySig.current.isEmpty()
        ? null
        : {
            signature: destroyedBySig.current.getSignature(),
            signer: data.destroyed_by.full_name,
            timestamp: moment().toISOString(),
            userAgent: window.navigator.userAgent,
            ip_address: ipAddress,
            monolith_user: {
              first_name: currentUser.first_name,
              last_name: currentUser.last_name,
              full_name: currentUser.full_name,
              email: currentUser.email,
              title: currentUser.title,
              user_id: currentUser.user_id,
            },
            esig: destroyedBySig.current.isESignature,
          };

      data.signature = JSON.stringify(signature);

      const requestObj = {
        caseInfo: {
          case_id: defaultInfo.case_id,
          case_number: defaultInfo.case_number,
          case_ref: defaultInfo.case_ref,
        },
        storageInfo: {
          storage_id: defaultInfo.storage_id,
          uuid: defaultInfo.uuid,
          storage_number: defaultInfo.storage_number,
          is_assigned: defaultInfo.is_assigned,
          case_assignment_id: defaultInfo.case_assignment_id,
        },
        moveData: {
          entered_by_id: currentUser.user_id,
          entered_by: currentUser.full_name,
          // The API overrides these with the Destroyed meta location for
          // coc_type "Destroy"; they are still sent so API versions without
          // that handling don't write a NULL location.
          new_location_id: LocationsAPI.metaLocations.destroyed.location_id,
          new_location_name: LocationsAPI.metaLocations.destroyed.location_name,
          current_location_name: data.destroyed_by.full_name,
          reason: data.reason || "Storage Item Destroyed",
          timestamp: db_timestamp(data.timestamp, true),
          signature: data.signature,
          coc_type: "Destroy",
        },
      };

      const result = await CaseStorageAPI.moveStorage(requestObj);

      if (!result?.success) {
        submitting.current = false;
        enqueueSnackbar(result?.message || "Failed to destroy storage item.", {
          variant: "error",
        });
        return;
      }

      submitting.current = false;

      handleClose();

      onSubmit(requestObj);

      enqueueSnackbar(`Storage Item Destroyed`, {
        variant: "success",
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") handleClose();
      }}
      style={{ zIndex: 1400 }}
    >
      <div
        style={{
          marginTop: 20,
          marginBottom: 20,
          width: 500,
          minHeight: 350,
          backgroundColor: theme.palette.background.default,
          position: "fixed",
          left: "calc(50% - 250px)",
          maxHeight: "calc(100vh - 100px)",
          overflowY: "auto",
          padding: 20,
          outline: "none",
        }}
      >
        <div style={{ marginBottom: 15, fontSize: "large" }}>
          Destroy Storage Item
        </div>
        {users && (
          <>
            <Form ref={form} colCount={1}>
              <SimpleItem
                dataField="destroyed_by"
                label={{ text: "Destroyed By" }}
                editorType="dxSelectBox"
                isRequired={true}
                editorOptions={{
                  dataSource: users,
                  searchEnabled: true,
                  displayExpr: "full_name",
                  dropDownOptions: {
                    maxHeight: 250,
                  },
                  itemTemplate: "destroyed_by",
                }}
              />
              <Template
                name="destroyed_by"
                render={(data) => <UserSelectBoxItem data={data} />}
              />
              <SimpleItem
                dataField="timestamp"
                label={{ text: "Timestamp" }}
                editorType="dxDateBox"
                isRequired={true}
                editorOptions={{
                  type: "datetime",
                  useMaskBehavior: true,
                  displayFormat: getDateFormat({
                    isMoment: false,
                    includeTime: true,
                  }),
                  value: new Date(),
                }}
                validationRules={[
                  {
                    type: "custom",
                    message: "Invalid Date",
                    validationCallback: validateTimestamp,
                  },
                ]}
              />
              <SimpleItem
                dataField="reason"
                label={{ text: "Destroy Notes" }}
                editorType={"dxTextArea"}
              />
              <SimpleItem
                dataField="custody_from_sig"
                label={{ visible: false }}
              >
                <SignatureField
                  sigFieldRef={destroyedBySig}
                  label="Destroyed By Signature"
                  useESignature={true}
                />
              </SimpleItem>
            </Form>
            <ToolBarItems
              submitText="Destroy Storage"
              onSubmit={handleSubmit}
              onCancel={() => handleClose()}
            />
          </>
        )}
        {!users && <Loader />}
      </div>
    </Modal>
  );
};
